import type { Dispatch, SetStateAction } from 'react';
import type { GQLLocale } from 'graphql-ts-types';
import { ArticleImageFormat } from 'graphql-ts-types';

import { EventBusEvent } from 'common/enums/EventBus';
import type { Locale } from 'common/enums/Locale';
import eventBus from 'common/event-bus/EventBus';
import { executeQuery } from 'common/gql';
import { toGQLLocale } from 'common/gql/utils/gql-locale';
import type {
  ReclamationRecapItem,
  WithArticleWithoutSettersProps,
} from 'common/types/reclamation';
import isWebpSupported from 'common/utils/isWebpSupported';
import type { ReclamationStepAdditionalInfoProps } from '../../../reclamation-drawer/components/reclamation-step-additional-info/ReclamationStepAdditionalInfo';
import type { ReclamationStepReasonsProps } from '../../../reclamation-drawer/components/ReclamationStepReasons';
import type { ReclamationStepSolutionsProps } from '../../../reclamation-drawer/components/ReclamationStepSolutions';
import type { CommonTrackingEventData } from '../../../reclamation-drawer/utils/tracking';
import recapItemsQuery from '../../gql/query/recapItems.gql';

type Step =
  | ReclamationStepReasonsProps
  | ReclamationStepSolutionsProps
  | ReclamationStepAdditionalInfoProps;

type StepsWithArticleWithoutSetters = Array<WithArticleWithoutSettersProps<Step>>;

type SetIsLoading = Dispatch<SetStateAction<boolean>>;

type SetItems = Dispatch<SetStateAction<ReclamationRecapItem[]>>;

interface GetUpdatedItemsOptions {
  items: ReclamationRecapItem[];
  itemId: string;
  comment?: string;
  resources?: string[];
}

interface GetFinishHandlerOptions {
  reclamationId: string;
  locale: Locale;
  setItems: SetItems;
  setIsLoading: SetIsLoading;
  isExternalMarketplace?: boolean;
}

interface OpenDrawerOptions {
  steps: StepsWithArticleWithoutSetters;
  reclamationId: string;
  locale: Locale;
  trackingEventData: CommonTrackingEventData;
  setItems: SetItems;
  setIsLoading: SetIsLoading;
  isExternalMarketplace?: boolean;
}

interface RecapItemsQueryVariables {
  reclamationId: string;
  locale: GQLLocale;
  isExternalMarketplace?: boolean;
}

interface RecapItemsQueryResponse {
  reclamation: {
    items: ReclamationRecapItem[];
  };
}

const getFinishHandler =
  ({
    reclamationId,
    locale,
    isExternalMarketplace,
    setItems,
    setIsLoading,
  }: GetFinishHandlerOptions) =>
  async () => {
    eventBus.emit(EventBusEvent.ReclamationDrawerToggle, null);
    setIsLoading(true);

    const {
      reclamation: { items },
    } = await executeQuery<RecapItemsQueryResponse, RecapItemsQueryVariables>({
      query: recapItemsQuery,
      variables: {
        reclamationId,
        locale: toGQLLocale(locale),
        ...(isExternalMarketplace && { isExternalMarketplace }),
        ...(isWebpSupported() && { imageFormat: ArticleImageFormat.Webp }),
      },
    });

    setItems(items);
    setIsLoading(false);
  };

export const openDrawer = ({
  steps,
  reclamationId,
  locale,
  trackingEventData,
  isExternalMarketplace = false,
  setItems,
  setIsLoading,
}: OpenDrawerOptions) => {
  eventBus.emit(EventBusEvent.ReclamationDrawerToggle, {
    isOpen: true,
    reclamationId,
    steps,
    trackingEventData,
    onFinish: getFinishHandler({
      reclamationId,
      locale,
      isExternalMarketplace,
      setItems,
      setIsLoading,
    }),
  });
};

export const getUpdatedItems = ({ items, itemId, comment, resources }: GetUpdatedItemsOptions) => {
  const newItems = [...items];
  const itemIndex = newItems.findIndex(({ id }) => id === itemId);
  newItems[itemIndex] = {
    ...newItems[itemIndex],
    ...((comment || comment === '') && { comment }),
    ...(resources && { resources }),
  };

  return newItems;
};
